import { useRef, useState } from "react";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import { FormattedMessage } from "react-intl";

import type { Theme } from "@mui/material/styles";

const useStyles = makeStyles((theme: Theme) => ({
  uploadInput: {
    position: "absolute" as const,
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    width: "100%",
    opacity: 0,
    cursor: "pointer",
  },
  uploadButton: {
    flexGrow: 1,
    width: "100%",
    backgroundColor: theme.palette.grey[300],
  },
  buttonLabel: {
    width: "100%",
  },
  filename: {
    textTransform: "none" as const,
    fontSize: 12,
  },
}));

type CfFileUploadProps = {
  btnTranslId?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const CfFileUpload = (props: CfFileUploadProps): JSX.Element => {
  const { btnTranslId = "common.select-file", onChange } = props;
  const classes = useStyles();

  const [filename, setFilename] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFilename(e.target.files[0] ? e.target.files[0].name : "");
      onChange(e);
    }
  };

  /**
   * WORKAROUND!!
   * FF has different target when clicking on a button with input inside than chrome
   */
  const clickUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLElement;
    if (target.localName === "button") {
      e.stopPropagation();
      fileInputRef.current?.click();
    }
  };

  const clearSelection = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setFilename("");
  };

  // Expose clearSelection method for external use
  (
    CfFileUpload as typeof CfFileUpload & { clearSelection?: () => void }
  ).clearSelection = clearSelection;

  return (
    <Button
      classes={{ root: classes.uploadButton }}
      id="file-name-button"
      onClick={clickUpload}
      sx={{ "& .MuiButton-label": classes.buttonLabel }}
    >
      {filename.length === 0 ? (
        <FormattedMessage id={btnTranslId} />
      ) : (
        <Typography className={classes.filename} noWrap={true}>
          {filename}
        </Typography>
      )}
      <input
        className={classes.uploadInput}
        id="file-name"
        onChange={handleChange}
        ref={fileInputRef}
        type="file"
      />
    </Button>
  );
};

export { CfFileUpload };
